/**
 * Environment variable validator
 * Fails fast if required environment variables are missing
 */

interface EnvConfig {
  name: string;
  required: boolean;
  validator?: (value: string) => boolean;
  errorMessage?: string;
}

// Matches a bare loopback origin with an optional port, e.g. `http://127.0.0.1:54321`
// or `http://localhost:54321`. Intentionally narrow (loopback host only) so a
// misconfigured non-Supabase http(s) URL can't slip through in a preview build.
const LOCAL_SUPABASE_URL_PATTERN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/;

const ENV_CONFIGS: EnvConfig[] = [
  {
    name: 'VITE_SUPABASE_URL',
    required: true,
    validator: (v) => {
      if (v.startsWith('https://') && v.includes('.supabase.co')) {
        return true;
      }

      // Outside production, also accept a local Supabase stack (`supabase start`
      // serves its API at http://127.0.0.1:54321 per supabase/config.toml). This
      // exception never applies to a production build, which must still use a
      // real https://*.supabase.co URL.
      //
      // NOTE: `import.meta.env.PROD` and `import.meta.env.MODE` are independent
      // signals in Vite and can diverge. `vite build` hardcodes NODE_ENV (and thus
      // PROD) to true regardless of `--mode`, so `vite build --mode development`
      // yields PROD === true with MODE === 'development'. Gating on PROD alone
      // would reject the loopback URL in that combination even though this is
      // exactly the non-production (dev/preview/e2e) build the CSP plugin in
      // vite.config.ts (gated on `mode !== 'production'`) already relaxed CSP for.
      // Treat the build as production only when BOTH signals agree it is
      // production; do not "simplify" this back to a single flag.
      if ((!import.meta.env.PROD || import.meta.env.MODE !== 'production') && LOCAL_SUPABASE_URL_PATTERN.test(v)) {
        return true;
      }

      return false;
    },
    errorMessage: 'Must be a valid Supabase URL',
  },
  {
    name: 'VITE_SUPABASE_ANON_KEY',
    required: true,
    validator: (v) => v.length > 100,
    errorMessage: 'Must be a valid Supabase anon key',
  },
];

// Note: Analytics secret validation moved to server-side (edge function)
// Client should not reference secrets - they are validated in sanitized-analytics function

interface ValidationResult {
  valid: boolean;
  missing: string[];
  invalid: { name: string; error: string }[];
}

function resolveEnvValue(name: string): string | undefined {
  if (name === 'VITE_SUPABASE_ANON_KEY') {
    return import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_KEY;
  }

  return import.meta.env[name];
}

export function validateEnvironment(): ValidationResult {
  const missing: string[] = [];
  const invalid: { name: string; error: string }[] = [];

  for (const config of ENV_CONFIGS) {
    const value = resolveEnvValue(config.name);

    if (config.required && !value) {
      missing.push(config.name);
      continue;
    }

    if (value && config.validator && !config.validator(value)) {
      invalid.push({
        name: config.name,
        error: config.errorMessage || 'Invalid value',
      });
    }
  }

  return {
    valid: missing.length === 0 && invalid.length === 0,
    missing,
    invalid,
  };
}

export function assertEnvironment(): void {
  const result = validateEnvironment();

  if (!result.valid) {
    const errors: string[] = [];

    if (result.missing.length > 0) {
      errors.push(`Missing required environment variables: ${result.missing.join(', ')}`);
    }

    if (result.invalid.length > 0) {
      errors.push(
        `Invalid environment variables: ${result.invalid
          .map((i) => `${i.name} (${i.error})`)
          .join(', ')}`
      );
    }

    const message = `Environment validation failed:\n${errors.join('\n')}`;
    
    console.error(message);
    
    // In production, throw to prevent app from starting with invalid config
    if (import.meta.env.PROD) {
      throw new Error(message);
    }
  }
}

// Export for use in diagnostics
export function getEnvironmentStatus(): {
  configured: string[];
  missing: string[];
  mode: string;
} {
  const result = validateEnvironment();
  
  return {
    configured: ENV_CONFIGS
      .filter((c) => resolveEnvValue(c.name))
      .map((c) => c.name),
    missing: result.missing,
    mode: import.meta.env.MODE,
  };
}
